import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search } from "lucide-react";
import { MaskedReveal, FadeUp } from "@/components/motion/Reveal";
import { FAQS } from "@/data/site";

function Item({ item, i, open, onToggle }) {
  return (
    <div className="border-t hairline">
      <button onClick={onToggle} className="w-full grid grid-cols-12 gap-4 py-6 text-left items-start" data-testid={`faq-q-${i}`}>
        <div className="col-span-1 mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</div>
        <div className="col-span-10 serif text-xl md:text-2xl">{item.q}</div>
        <div className="col-span-1 justify-self-end">
          <motion.div animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.4 }}>
            <Plus className="h-5 w-5" />
          </motion.div>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: [0.7, 0.05, 0.2, 1] }} className="overflow-hidden">
            <div className="grid grid-cols-12 gap-4 pb-8">
              <div className="col-span-1" />
              <div className="col-span-10 md:col-span-8 text-[15px] text-[hsl(var(--muted-foreground))] leading-relaxed">{item.a}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  const [q, setQ] = useState("");
  const filtered = FAQS.filter(f => f.q.toLowerCase().includes(q.toLowerCase()) || f.a.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="faq-page">
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 00 · Frequently Asked</div>
          <MaskedReveal as="h1" className="serif text-6xl md:text-[9vw] leading-[0.92]" lines={["Answers,", <em className="italic text-gold">on the record.</em>]} />
          <FadeUp delay={0.35} className="max-w-2xl mt-10 text-[hsl(var(--muted-foreground))]">
            A candid FAQ across our practice areas. If your question is not here, write to us — we respond within one working day.
          </FadeUp>

          <div className="mt-10 max-w-lg flex items-center gap-3 border-b hairline">
            <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search FAQs"
              className="flex-1 bg-transparent py-3 text-sm focus:outline-none placeholder:text-[hsl(var(--muted-foreground))]"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          {filtered.map((f, i) => (
            <Item key={i} i={i} item={f} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
          ))}
          {filtered.length === 0 && (
            <div className="py-24 text-center text-[hsl(var(--muted-foreground))]">No matching question found.</div>
          )}
        </div>
      </section>
    </div>
  );
}
